/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Collapse, Form, Flex, Row, Col, Space, Card, message, Modal } from "antd";
import { Input, Button, Table, Typography, DatePicker, Tag } from "antd";
import { SearchOutlined, ClearOutlined, FormOutlined, FileSyncOutlined } from "@ant-design/icons";
import { MdOutlineLibraryAdd } from "react-icons/md";
import { accessColumn, invCodeOf, isDraft } from "./model";
import useColumnSettings from "../../../hooks/useColumnSettings";
import useAccessPageState from "../../../hooks/useAccessPageState";
import dayjs from "dayjs";
import ProformaService from "../../../service/ERP/Proforma.service";
import "../invoice/MyPage.css";
import "./proforma.css";

const piService = ProformaService();
const { RangePicker } = DatePicker;

const mngConfig = { title: "", action: "create", code: null };

const ProformaAccess = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [accessData, setAccessData] = useState([]);
  const [activeSearch, setActiveSearch] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const {
    tablePagination,
    saveState,
    restoreState,
    clearState,
    restoreDefaultPagination,
    handleTableChange,
  } = useAccessPageState({ form });

  const handleSearch = (forcedValues = null, paginationOverride = null) => {
    const runSearch = (v) => {
      saveState(v, paginationOverride ?? tablePagination);
      const data = { ...v };
      if (data?.pi_date) {
        const [pi_date_form, pi_date_to] = data.pi_date.map((m) => dayjs(m).format("YYYY-MM-DD"));
        Object.assign(data, { pi_date_form, pi_date_to });
        delete data.pi_date;
      }
      piService
        .search(data, { ignoreLoading: Object.keys(data).length !== 0 })
        .then((res) => setAccessData(res.data?.data || []))
        .catch(() => message.error("Request error!"));
    };

    if (useAccessPageState.canUseForcedSearchValues?.(forcedValues)) {
      runSearch(forcedValues);
      return;
    }
    form.validateFields().then((v) => runSearch(v));
  };

  const handleClear = () => {
    clearState();
    form.resetFields();
    handleSearch({}, restoreDefaultPagination());
  };

  /* วิธีที่ 1 : สร้างเอง กรอกชื่อสินค้าได้เลย */
  const handleAddManual = () => {
    navigate("manage/create", {
      state: {
        config: { ...mngConfig, title: "สร้าง Proforma Invoice", action: "create", source: "MANUAL" },
      },
    });
  };

  /* วิธีที่ 2 : ดึงข้อมูลมาจาก Invoice */
  const handleAddFromInvoice = () => {
    navigate("manage/create", {
      state: {
        config: { ...mngConfig, title: "สร้างจาก Invoice", action: "create", source: "INVOICE" },
      },
    });
  };

  const handleView = (record) => {
    navigate("manage/view", {
      state: {
        config: { ...mngConfig, title: "View Proforma Invoice", action: "view", code: record?.pi_code },
      },
    });
  };

  const handleEdit = (record) => {
    navigate("manage/edit", {
      state: {
        config: { ...mngConfig, title: "แก้ไข Proforma Invoice", action: "edit", code: record?.pi_code },
      },
    });
  };

  const handleDelete = async (record) => {
    try {
      await piService.deleted(record?.pi_code);
      message.success("ยกเลิกเอกสารสำเร็จ");
      handleSearch();
    } catch (err) {
      message.error(err?.response?.data?.message || "ยกเลิกไม่สำเร็จ");
    }
  };

  const handlePrint = (record) => {
    window.open(`/pi-print/${record?.pi_code}`, "_blank");
  };

  /* ออกเลขเอกสาร + จองเลข Invoice / ยกเลิกการจอง */
  const handleReserve = (record) => {
    const reserved = Number(record?.inv_reserved) === 1;
    const draft = isDraft(record);
    const target = invCodeOf(record?.runno);

    Modal.confirm({
      title: reserved ? "ยกเลิกการจองเลข Invoice" : draft ? "ออกเลขเอกสาร" : "จองเลข Invoice",
      content: reserved
        ? `ยกเลิกการจองเลข ${target} ของ ${record?.pi_code} ใช่หรือไม่?`
        : draft
        ? "ระบบจะออกเลข PI ให้ใบนี้ พร้อมจองเลข Invoice ที่คู่กัน — ออกแล้วเปลี่ยนกลับเป็นใบร่างไม่ได้"
        : `จองเลข ${target} ไว้ให้ ${record?.pi_code} เมื่อออก Invoice จากใบนี้จะได้เลขนี้`,
      okText: reserved ? "ยกเลิกการจอง" : draft ? "ออกเลขเอกสาร" : "จองเลข",
      cancelText: "ปิด",
      okButtonProps: reserved ? { danger: true } : {},
      onOk: async () => {
        try {
          if (reserved) {
            await piService.cancelReserve(record.pi_code);
            message.success("ยกเลิกการจองแล้ว");
          } else {
            const res = await piService.reserveInvoice(record.pi_code);
            const d = res.data?.data || {};
            message.success(
              draft
                ? `ออกเลข ${d.pi_code} แล้ว (จอง ${d.inv_code} ไว้ให้)`
                : `จองเลข ${d.inv_code || target} แล้ว`
            );
          }
          handleSearch();
        } catch (err) {
          message.error(err?.response?.data?.message || "ทำรายการไม่สำเร็จ");
        }
      },
    });
  };

  useEffect(() => {
    const restored = restoreState();
    handleSearch(restored?.searchValues ?? null, restored?.tablePagination);
  }, []);

  const FormSearch = (
    <Collapse
      size="small"
      bordered={false}
      activeKey={activeSearch}
      onChange={setActiveSearch}
      items={[
        {
          key: "1",
          label: <><SearchOutlined /><span> ค้นหา</span></>,
          showArrow: false,
          children: (
            <>
              <br />
              <Form form={form} layout="vertical" autoComplete="off">
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="เลขที่ PI" name="pi_code">
                      <Input placeholder="เช่น PI260001" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="เลขที่ Invoice" name="inv_code">
                      <Input placeholder="เช่น NSF0001" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="ชื่อลูกค้า" name="cusname">
                      <Input placeholder="Enter Customer Name" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Brand" name="brand">
                      <Input placeholder="Enter Brand" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="วันที่เอกสาร" name="pi_date">
                      <RangePicker
                        placeholder={["From Date", "To Date"]}
                        style={{ width: "100%", height: 40 }}
                        onChange={handleSearch}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={12} />
                  <Col xs={24} sm={12}>
                    <Flex justify="flex-end" gap={8}>
                      <Button type="primary" size="small" className="bn-action" icon={<SearchOutlined />} onClick={() => handleSearch()}>ค้นหา</Button>
                      <Button type="primary" size="small" className="bn-action" danger icon={<ClearOutlined />} onClick={handleClear}>ล้าง</Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </>
          ),
        },
      ]}
    />
  );

  const allColumns = accessColumn({ handleView, handleEdit, handleDelete, handlePrint, handleReserve });

  /* คอลัมน์ที่แสดง ผู้ใช้เลือกเองได้ ระบบจำไว้ใน cookie ของเครื่องนี้ */

  const { columns: column, settingsButton } = useColumnSettings(

    "proforma_access_cols",

    allColumns

  );

  const TitleTable = (
    <Flex className="width-100" align="center">
      <Col span={12} className="p-0">
        <Typography.Title className="m-0 !text-zinc-800" level={3}>
          Proforma Invoice
        </Typography.Title>
      </Col>
      <Col span={12} style={{ paddingInline: 0 }}>
        <Flex gap={8} justify="end">
          {settingsButton}
          <Button
            size="small"
            className="bn-action bn-center bn-primary-outline justify-center"
            icon={<MdOutlineLibraryAdd style={{ fontSize: ".9rem" }} />}
            onClick={() => setOpenCreate(true)}
          >
            สร้าง Proforma Invoice
          </Button>
        </Flex>
      </Col>
    </Flex>
  );

  return (
    <div className="invoice-access">
      <Space direction="vertical" size="middle" style={{ display: "flex", position: "relative" }}>
        {FormSearch}
        <Card>
          <Row gutter={[8, 8]} className="m-0">
            <Col xs={24}>
              <Table
                title={() => TitleTable}
                size="small"
                rowKey="pi_code"
                columns={column}
                dataSource={accessData}
                scroll={{ x: "max-content" }}
                pagination={tablePagination}
                onChange={handleTableChange}
              />
            </Col>
          </Row>
        </Card>
      </Space>

      <Modal
        open={openCreate}
        onCancel={() => setOpenCreate(false)}
        footer={null}
        width={640}
        centered
        title="สร้าง Proforma Invoice"
        className="pi-create-modal"
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          เลือกวิธีที่จะเริ่มสร้างเอกสาร
        </Typography.Paragraph>

        <div className="pi-create-grid">
          <div
            role="button"
            tabIndex={0}
            className="pi-create-card"
            onClick={() => { setOpenCreate(false); handleAddManual(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setOpenCreate(false); handleAddManual(); } }}
          >
            <span className="pi-create-ico"><FormOutlined /></span>
            <span className="pi-create-title">กรอกเอง</span>
            <Tag color="green" className="pi-create-tag">ไม่ต้องใช้รหัสสินค้า</Tag>
            <span className="pi-create-desc">
              พิมพ์ชื่อสินค้าลงไปได้เลย ไม่ต้องเลือกจากระบบ
              ค่อยจับคู่รหัสสินค้าตอนนำไปออก Invoice
            </span>
          </div>

          <div
            role="button"
            tabIndex={0}
            className="pi-create-card is-blue"
            onClick={() => { setOpenCreate(false); handleAddFromInvoice(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setOpenCreate(false); handleAddFromInvoice(); } }}
          >
            <span className="pi-create-ico"><FileSyncOutlined /></span>
            <span className="pi-create-title">ดึงข้อมูลจาก Invoice</span>
            <Tag color="blue" className="pi-create-tag">ใช้เลขรันเดียวกัน</Tag>
            <span className="pi-create-desc">
              คัดลอกข้อมูลจากใบ Invoice ที่มีอยู่ทั้งใบ
              และผูกเลขรัน 4 หลักให้ตรงกันทันที
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProformaAccess;
